import { useState } from 'react';
import type { ErrorField } from '../types/ErrorField';
import type { AddCaseExpenseValue } from './useLegalCaseExpenses';

// Campos com binding próprio na tela; qualquer outro 422 do backend cai no bloco residual.
const INLINE_FIELDS = new Set([
    'category',
    'amount',
    'paidAt',
    'beneficiaryId',
    'description',
]);

export const residualErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !INLINE_FIELDS.has(e.fieldName));

// O valor usa CurrencyInput (numérico); 0 = "não informado" (form começa zerado), então não é
// enviado — o backend recusa (422) e a mensagem aparece no campo.
const moneyOrUndefined = (value: number): number | undefined =>
    value && value > 0 ? value : undefined;

interface UseLegalCaseExpenseFormArgs {
    onSubmit: (value: AddCaseExpenseValue) => Promise<boolean>;
    defaultCategory: string;
    // Pré-seleção do beneficiário (o escritório responsável do caso). Ajuda de UX.
    defaultBeneficiaryId?: string;
}

// Estado e handlers do formulário de despesa (na seção Custos do caso), fora do componente
// (diretriz do repo: sem função dentro de função). O caso vem do contexto da seção, não é campo.
export default function useLegalCaseExpenseForm({
    onSubmit,
    defaultCategory,
    defaultBeneficiaryId,
}: UseLegalCaseExpenseFormArgs) {
    const [category, setCategory] = useState(defaultCategory);
    const [amount, setAmount] = useState(0);
    const [paidAt, setPaidAt] = useState('');
    const [beneficiaryId, setBeneficiaryId] = useState(defaultBeneficiaryId || '');
    const [description, setDescription] = useState('');

    const reset = () => {
        setCategory(defaultCategory);
        setAmount(0);
        setPaidAt('');
        setBeneficiaryId(defaultBeneficiaryId || '');
        setDescription('');
    };

    // O valor é obrigatório, mas o botão fica sempre habilitado — o feedback vem do backend
    // (422), como nas demais seções.
    const canSubmit = true;

    const submit = async () => {
        const ok = await onSubmit({
            category,
            amount: moneyOrUndefined(amount),
            paidAt: paidAt.trim() || undefined,
            beneficiaryId: beneficiaryId.trim() || undefined,
            description: description.trim() || undefined,
        });
        if (ok) reset();
    };

    return {
        category, setCategory,
        amount, setAmount,
        paidAt, setPaidAt,
        beneficiaryId, setBeneficiaryId,
        description, setDescription,
        canSubmit, submit, reset,
    };
}
